/**
 * Cross-page resolver for the {attachments} macro.
 *
 * The legacy macro supports `{attachments:page=Some Other Page}` to render the
 * attachments of a DIFFERENT page than the one hosting the macro. AGG has no
 * "attachments by page title" field, so we resolve the title → contentId first,
 * then feed that id into the main `useAttachmentsMacroDataQuery`.
 *
 * Dual-lookup strategy (matching legacy backend behavior):
 * The legacy `page=` parameter uses `indexOf(':')` to split "SPACEKEY:Page Title",
 * but a colon may legitimately appear in a page title (e.g. "WIP:My Page").
 * To handle this, we fire TWO lookups in a single GraphQL request:
 *   - `rawContent`:   treat the entire string as a bare title in the host space
 *   - `splitContent`: treat the part before the first colon as a space key
 * We prefer the split interpretation; if it returns nothing (the parsed
 * "space key" doesn't correspond to a real space), we fall back to rawContent.
 * This matches the legacy Java backend's behavior exactly.
 *
 * Caveat (GRAPHQL_PLAN.md §8.4 / ADR-9b): AGG matches title EXACTLY, while the
 * legacy macro is case-insensitive. We request first:1 and accept the single
 * returned node; callers do a defensive case-insensitive title comparison.
 */

import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

export const USE_ATTACHMENTS_PAGE_BY_TITLE_QUERY: GraphQLTaggedNode = graphql`
	query useAttachmentsPageByTitleQuery(
		$rawSpaceKey: String!
		$rawTitle: String!
		$splitSpaceKey: String!
		$splitTitle: String!
		$hasSplit: Boolean!
	) {
		rawContent: content(spaceKey: $rawSpaceKey, title: $rawTitle, first: 1) {
			nodes {
				id
				title
			}
		}
		splitContent: content(spaceKey: $splitSpaceKey, title: $splitTitle, first: 1)
			@include(if: $hasSplit) {
			nodes {
				id
				title
			}
		}
	}
`;

export type ParsedPageRef = {
	/** The full raw string treated as a bare title in the host space. */
	rawTitle: string;
	/** The host space key (used for the raw lookup). */
	rawSpaceKey: string | undefined;
	/** The space key parsed from before the first colon (only when hasSplit is true). */
	splitSpaceKey: string;
	/** The title parsed from after the first colon (only when hasSplit is true). */
	splitTitle: string;
	/** Whether the raw value contained a colon that produced a split interpretation. */
	hasSplit: boolean;
};

/**
 * Parse a raw `page=` macro value into both a "raw" and a "split" interpretation.
 *
 * Supports the legacy "SPACEKEY:Page Title" form as well as a bare "Page Title"
 * (which falls back to the host page's space). The caller issues both queries
 * and picks the best match — split preferred, raw as fallback.
 */
export const parsePageRef = (pageRef: string, hostSpaceKey: string | undefined): ParsedPageRef => {
	const rawTitle = pageRef.trim();
	const colonIdx = pageRef.indexOf(':');

	if (colonIdx > 0) {
		const candidate = pageRef.slice(0, colonIdx).trim();
		const afterColon = pageRef.slice(colonIdx + 1).trim();

		if (candidate && afterColon) {
			return {
				rawTitle,
				rawSpaceKey: hostSpaceKey,
				splitSpaceKey: candidate,
				splitTitle: afterColon,
				hasSplit: true,
			};
		}
	}

	return {
		rawTitle,
		rawSpaceKey: hostSpaceKey,
		splitSpaceKey: '',
		splitTitle: '',
		hasSplit: false,
	};
};
